import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Brain, Download, CheckCircle2, CalendarPlus, Trash2, Share2, ChevronUp, ChevronDown, PlusCircle, BookOpen, Activity, Target, Star } from "lucide-react";
import { Assessments, Appointments, Patients, isNotFound } from "@/lib/api";
import LoadError from "@/components/LoadError";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LandmarkCanvas from "@/components/LandmarkCanvas";
import PinchZoomFrame from "@/components/PinchZoomFrame";
import ScheduleSessionsDialog from "@/components/ScheduleSessionsDialog";
import PlumbRuler from "@/components/PlumbRuler";
import ClinicalReasoningPanel from "@/components/ClinicalReasoningPanel";
import RedFlagsPanel from "@/components/RedFlagsPanel";
import VirtualBiomechanicalMap from "@/components/VirtualBiomechanicalMap";
import PostureScoreRing from "@/components/PostureScoreRing";
import EvolutionInsights from "@/components/EvolutionInsights";
import Disclaimer from "@/components/Disclaimer";
import VasScale from "@/components/VasScale";
import ShareDialog from "@/components/ShareDialog";
import ConsentNotice from "@/components/ConsentNotice";
import AddFromLibraryDialog from "@/components/AddFromLibraryDialog";
import FeatureGate from "@/components/FeatureGate";
import { detectPose, warmupPose } from "@/lib/poseDetector";
import { computeSagittal } from "@/lib/sagittal";
import SagittalAlignmentCard from "@/components/SagittalAlignmentCard";
import SessionContextPanel from "@/components/SessionContextPanel";
import PatientSummaryEditor from "@/components/PatientSummaryEditor";

const SEG_PT = {
  feet: "Pés", knees: "Joelhos", pelvis: "Bacia",
  spine: "Coluna (frontal)", spine_lateral: "Curvas laterais",
  shoulders: "Ombros", cervical: "Cervical",
  scapula: "Omoplatas",
};

const isPatientVisible = (item) => item?.patient_visible !== false;
const syncFlatPlanVisibility = (plan, item, value) => {
  const identifier = item?.library_id ? "library_id" : item?.key ? "key" : null;
  if (!identifier) return;
  const flatItem = (plan.techniques || []).find((candidate) => candidate[identifier] === item[identifier]);
  if (flatItem) flatItem.patient_visible = value;
};

function SeverityBadge({ sev }) {
  const map = {
    normal: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
    moderado: "text-amber-300 bg-amber-400/10 border-amber-400/30",
    significativo: "text-rose-300 bg-rose-400/10 border-rose-400/30",
  };
  return <span className={`text-xs px-2 py-1 rounded-full border ${map[sev] || "text-slate-300 border-slate-700"}`}>{sev}</span>;
}

export default function AssessmentWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState("");
  const [activeTab, setActiveTab] = useState("landmarks");
  const [autoConfidence, setAutoConfidence] = useState(null);
  const [silhouetteWarn, setSilhouetteWarn] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [libCtx, setLibCtx] = useState(null);  // null | {type:'clinical', phaseIdx} | {type:'home', groupIdx}
  const [appointments, setAppointments] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [patientLite, setPatientLite] = useState(null); // height/weight/bmi para hints e ruler
  const [heatmapOn, setHeatmapOn] = useState(false);     // Fase 4 — toggle heatmap (default off)
  const [focusModeOn, setFocusModeOn] = useState(() => {
    try { return localStorage.getItem("mio:focusMode") === "1"; } catch { return false; }
  });
  // Persiste preferência de Focus Mode entre avaliações.
  useEffect(() => {
    try { localStorage.setItem("mio:focusMode", focusModeOn ? "1" : "0"); } catch { /* */ }
  }, [focusModeOn]);
  // Grid overlay — fonte primária: assessment.grid_settings (persistido em DB para portal+PDF);
  // fallback: localStorage; default: ON / 32px / 18%.
  const [gridSettings, setGridSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mio_grid") || "{}"); }
    catch { return {}; }
  });
  const grid = {
    show: a?.grid_settings?.show ?? gridSettings.show ?? true,
    size: a?.grid_settings?.size ?? gridSettings.size ?? 32,
    opacity: a?.grid_settings?.opacity ?? gridSettings.opacity ?? 0.18,
    mode: a?.grid_settings?.mode ?? gridSettings.mode ?? "analysis",
  };
  const gridSaveTimer = useRef(null);
  const updateGrid = (patch) => {
    const next = { ...grid, ...patch };
    setGridSettings(next);
    try { localStorage.setItem("mio_grid", JSON.stringify(next)); } catch { /* */ }
    // Persist no assessment (debounced) — para que portal e PDF reutilizem.
    if (id) {
      if (gridSaveTimer.current) clearTimeout(gridSaveTimer.current);
      gridSaveTimer.current = setTimeout(() => {
        Assessments.setGrid(id, next).catch(() => {/* silencioso */});
      }, 400);
    }
  };

  const [loadErr, setLoadErr] = useState(false);
  const load = async () => {
    setLoadErr(false);
    try {
      const d = await Assessments.get(id);
      if (!d) { setLoadErr(true); return; }
      setA(d);
    }
    catch (e) {
      if (!isNotFound(e)) { setLoadErr(true); return; }
      toast.error("Avaliação não encontrada"); nav("/");
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);
  // Carregar appointments ligados a esta avaliação (para badge de estado do plano).
  // Filter client-side (endpoint não tem assessment_id query — evita-se backend change).
  const loadAppointments = async () => {
    try {
      const all = await Appointments.list();
      setAppointments((all || []).filter(ap => ap.assessment_id === id));
    } catch { /* silencioso — badge degrada para "não agendado" */ }
  };
  useEffect(() => { if (id) loadAppointments(); /* eslint-disable-line */ }, [id]);
  // Quando voltamos da Matrix Library com contexto guardado, ativa a tab
  // "Análise Clínica" e restaura a posição de scroll. Sem refactor de routing
  // — apenas leitura leve de sessionStorage.
  useEffect(() => {
    if (!a) return;
    let raw = null;
    try { raw = sessionStorage.getItem("mio:matrix:back"); } catch { /* */ }
    if (!raw) return;
    let ctx = null;
    try { ctx = JSON.parse(raw); } catch { ctx = null; }
    if (!ctx || ctx.assessmentId !== id) return;
    setActiveTab("spine");
    const t = setTimeout(() => {
      try { window.scrollTo({ top: ctx.scrollY || 0, behavior: "smooth" }); } catch { /* */ }
      try { sessionStorage.removeItem("mio:matrix:back"); } catch { /* */ }
    }, 120);
    return () => clearTimeout(t);
  }, [a, id]);
  // Carrega dados antropométricos do paciente (height/weight/bmi/gender) — usados
  // pelo Body Depth Factor e pela Régua biomecânica.
  useEffect(() => {
    if (!a?.patient_id) return;
    Patients.get(a.patient_id).then(setPatientLite).catch(() => {});
  }, [a?.patient_id]);
  // Pré-aquecer modelo MediaPipe enquanto o utilizador olha para a imagem
  useEffect(() => { warmupPose(); }, []);

  if (!a && loadErr) return <LoadError testId="assessment-load-error" onRetry={load} />;
  if (!a) return <div className="text-slate-500" data-testid="assessment-loading">A carregar…</div>;
  const sagittal = computeSagittal(a.landmarks_side, a.sagittal_orientation);
  const setOrientation = async (v) => {
    try {
      const saved = await Assessments.saveLandmarks(id, { sagittal_orientation: v });
      // Preserva pontos editados localmente (ainda não guardados) — só atualiza a orientação.
      setA((prev) => ({ ...prev, sagittal_orientation: saved.sagittal_orientation || "" }));
      toast.success("Orientação guardada");
    }
    catch { toast.error("Falha ao guardar a orientação"); }
  };

  const hasLandmarks = Object.keys(a.landmarks_front || {}).length + Object.keys(a.landmarks_side || {}).length > 0;

  const detectAI = async () => {
    setBusy("ai");
    setAutoConfidence(null);
    setSilhouetteWarn(false);
    try {
      toast.info("MediaPipe a analisar a imagem…", { description: "Modelo HEAVY · 100% local" });

      const views = [
        { key: "landmarks_front", img: a.front_image, view: "front" },
        { key: "landmarks_side",  img: a.side_image,  view: "side"  },
        { key: "landmarks_back",  img: a.back_image,  view: "back"  },
      ].filter(v => v.img);

      // Hints antropométricos para o refinamento da silhueta lateral
      // (Body Depth Factor — Fase 1). Reusa `patientLite` já carregado;
      // se ainda não estiver disponível, faz fetch on-demand.
      let patientHints = null;
      try {
        const pat = patientLite || (a.patient_id ? await Patients.get(a.patient_id) : null);
        if (pat) {
          patientHints = {
            height_cm: pat.height_cm ?? null,
            weight_kg: pat.weight_kg ?? null,
            bmi: pat.bmi ?? null,
            gender: pat.gender ?? null,
          };
        }
      } catch { /* sem hints — fallback ao default */ }

      const results = await Promise.all(
        views.map(v => detectPose(v.img, v.view, patientHints).then(r => ({ ...v, ...r })))
      );

      const detected = results.filter(r => r.detected);
      const updates = {};
      let totalConf = 0;
      let totalPts = 0;

      for (const r of detected) {
        // Preserva pontos manuais existentes, sobrepondo só os auto-detetados.
        const existing = a[r.key] || {};
        updates[r.key] = { ...existing, ...r.landmarks };
        totalConf += r.confidence * r.points;
        totalPts += r.points;
      }

      const overallConf = totalPts > 0 ? totalConf / totalPts : 0;

      // Fallback servidor (Claude) se MediaPipe não detetar pose ou confiança baixa em todas vistas
      const needsFallback = detected.length === 0 || (overallConf < 0.4 && detected.length < views.length);

      if (needsFallback) {
        toast.message("A complementar com IA do servidor…", { description: "MediaPipe não cobriu todos os pontos" });
        const out = await Assessments.detect(id);
        setA((prev) => ({ ...prev, ...out }));
        toast.success("Pontos sugeridos carregados (IA do servidor)");
        return;
      }

      // Persistir
      const persisted = await Assessments.saveLandmarks(id, {
        landmarks_front: updates.landmarks_front || a.landmarks_front || {},
        landmarks_side:  updates.landmarks_side  || a.landmarks_side  || {},
        landmarks_back:  updates.landmarks_back  || a.landmarks_back  || {},
      });
      setA(persisted);
      setAutoConfidence(overallConf);
      // Sinaliza confiança baixa do refinamento da silhueta lateral (se aplicável)
      const sideRes = detected.find(r => r.view === "side");
      const silConf = sideRes?.silhouette_confidence;
      setSilhouetteWarn(silConf != null && silConf < 0.75);

      const pct = Math.round(overallConf * 100);
      const totalDetected = detected.reduce((s, r) => s + r.points, 0);
      toast.success(`MediaPipe: ${totalDetected} pontos detetados`, {
        description: `Confiança média ${pct}% · ${detected.map(r => r.view).join(" · ")}`,
      });
    } catch (e) {
      console.error(e);
      // Último recurso — servidor
      try {
        const out = await Assessments.detect(id);
        setA((prev) => ({ ...prev, ...out }));
        toast.warning("MediaPipe falhou, usei a IA do servidor");
      } catch {
        toast.error("Falha na deteção de pontos");
      }
    } finally { setBusy(""); }
  };

  const saveLandmarks = async () => {
    setBusy("save");
    try {
      await Assessments.saveLandmarks(id, {
        landmarks_front: a.landmarks_front,
        landmarks_side: a.landmarks_side,
        landmarks_back: a.landmarks_back,
      });
      toast.success("Pontos guardados");
    } catch { toast.error("Falha ao guardar"); }
    finally { setBusy(""); }
  };

  const runAnalysis = async () => {
    if (!hasLandmarks) { toast.error("Marca os pontos antes de analisar"); return; }
    setBusy("analyze");
    try {
      await Assessments.saveLandmarks(id, {
        landmarks_front: a.landmarks_front,
        landmarks_side: a.landmarks_side,
        landmarks_back: a.landmarks_back,
      });
      const updated = await Assessments.analyze(id);
      setA(updated);
      setActiveTab("results");
      toast.success("Análise concluída");
    } catch (e) {
      toast.error("Erro ao correr a análise");
    } finally { setBusy(""); }
  };

  // Fase 1.2: agendamento manual via dialog. Já não há geração automática de datas
  // — o terapeuta seleciona linhas, define data/hora e confirma.
  const openScheduleDialog = () => {
    if (!(a?.treatment_plan?.sessions || []).length) {
      toast.info("Adiciona sessões ao plano antes de agendar");
      return;
    }
    setScheduleOpen(true);
  };

  const removeTechnique = async (phaseIdx, techIdx) => {
    const plan = { ...(a.treatment_plan || {}) };
    const phases = [...(plan.clinical_phases || [])];
    const phase = { ...phases[phaseIdx] };
    phase.techniques = (phase.techniques || []).filter((_, i) => i !== techIdx);
    phases[phaseIdx] = phase;
    plan.clinical_phases = phases;
    // Manter compatibilidade com flat techniques (apenas as clínicas)
    plan.techniques = phases.flatMap((p) => p.techniques || []);
    setA({ ...a, treatment_plan: plan });
    await Assessments.updateTreatmentPlan(id, plan);
    toast.success("Técnica removida");
  };

  const removeHomeExercise = async (groupIdx, exIdx) => {
    const plan = { ...(a.treatment_plan || {}) };
    const home = [...(plan.home_plan || [])];
    const grp = { ...home[groupIdx] };
    grp.exercises = (grp.exercises || []).filter((_, i) => i !== exIdx);
    home[groupIdx] = grp;
    plan.home_plan = home;
    setA({ ...a, treatment_plan: plan });
    await Assessments.updateTreatmentPlan(id, plan);
    toast.success("Exercício removido");
  };

  // ---- Plan editing helpers ----
  const saveTreatmentPlan = async (plan) => {
    setA((prev) => ({ ...prev, treatment_plan: plan }));
    try { await Assessments.updateTreatmentPlan(id, plan); }
    catch { toast.error("Não foi possível guardar o plano"); }
  };

  const moveTechnique = (phaseIdx, techIdx, dir) => {
    const plan = structuredClone(a.treatment_plan || {});
    const phases = plan.clinical_phases || [];
    const ph = phases[phaseIdx]; if (!ph) return;
    const arr = ph.techniques || [];
    const j = techIdx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[techIdx], arr[j]] = [arr[j], arr[techIdx]];
    saveTreatmentPlan(plan);
  };

  const moveHomeExercise = (groupIdx, exIdx, dir) => {
    const plan = structuredClone(a.treatment_plan || {});
    const home = plan.home_plan || [];
    const grp = home[groupIdx]; if (!grp) return;
    const arr = grp.exercises || [];
    const j = exIdx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[exIdx], arr[j]] = [arr[j], arr[exIdx]];
    saveTreatmentPlan(plan);
  };

  const addCustomTechnique = (phaseIdx) => {
    const name = window.prompt("Nome da técnica:");
    if (!name) return;
    const plan = structuredClone(a.treatment_plan || {});
    const phases = plan.clinical_phases || [];
    const ph = phases[phaseIdx]; if (!ph) return;
    ph.techniques = [...(ph.techniques || []), {
      name, category: "Personalizada", description: "", objective: "", target_area: "", indication: "", custom: true,
      patient_visible: false,
    }];
    saveTreatmentPlan(plan);
    toast.success("Técnica personalizada adicionada");
  };

  const addCustomHomeExercise = (groupIdx) => {
    const name = window.prompt("Nome do exercício:");
    if (!name) return;
    const plan = structuredClone(a.treatment_plan || {});
    const home = plan.home_plan || [];
    const grp = home[groupIdx]; if (!grp) return;
    grp.exercises = [...(grp.exercises || []), {
      name, objective: "", target_area: "", reps: "", frequency: "", custom: true,
      patient_visible: false,
    }];
    saveTreatmentPlan(plan);
    toast.success("Exercício personalizado adicionado");
  };

  const addItemFromLibrary = (item) => {
    const plan = structuredClone(a.treatment_plan || {});
    if (libCtx?.type === "clinical") {
      const phases = plan.clinical_phases || [];
      const ph = phases[libCtx.phaseIdx]; if (!ph) return;
      ph.techniques = [...(ph.techniques || []), {
        name: item.name, category: item.category || "Biblioteca",
        description: item.description || "", objective: item.objective || "",
        target_area: item.body_region || "", indication: item.instructions || "",
        instructions: item.instructions || "", image_url: item.image_url || "", video_url: item.video_url || "",
        library_id: item.id, patient_visible: false, professional_only: !!item.professional_only,
      }];
    } else if (libCtx?.type === "home") {
      const home = plan.home_plan || [];
      const grp = home[libCtx.groupIdx]; if (!grp) return;
      grp.exercises = [...(grp.exercises || []), {
        name: item.name, objective: item.objective || "",
        target_area: item.body_region || "",
        reps: item.repetitions || "8–12", frequency: "3–4×/sem",
        instructions: item.instructions || "", image_url: item.image_url || "", video_url: item.video_url || "",
        library_id: item.id, patient_visible: false,
      }];
    }
    saveTreatmentPlan(plan);
    toast.success("Adicionado ao plano");
  };

  const updateTechniqueField = (phaseIdx, techIdx, field, value) => {
    const plan = structuredClone(a.treatment_plan || {});
    const phases = plan.clinical_phases || [];
    const ph = phases[phaseIdx]; if (!ph) return;
    const arr = ph.techniques || [];
    if (!arr[techIdx]) return;
    arr[techIdx][field] = value;
    if (field === "patient_visible") syncFlatPlanVisibility(plan, arr[techIdx], value);
    saveTreatmentPlan(plan);
  };

  const updateHomeExerciseField = (groupIdx, exerciseIdx, field, value) => {
    const plan = structuredClone(a.treatment_plan || {});
    const groups = plan.home_plan || [];
    const group = groups[groupIdx];
    if (!group?.exercises?.[exerciseIdx]) return;
    group.exercises[exerciseIdx][field] = value;
    if (field === "patient_visible") syncFlatPlanVisibility(plan, group.exercises[exerciseIdx], value);
    saveTreatmentPlan(plan);
  };

  const addSession = async () => {
    const plan = { ...(a.treatment_plan || { sessions: [] }) };
    plan.sessions = [...(plan.sessions || []), {
      session_number: (plan.sessions?.length || 0) + 1,
      type: "mensal",
      focus: "Sessão adicional",
      // scheduled_at intencionalmente omitido — agendamento real fica para Fase 1.2 (dialog manual).
      // Compatibilidade: legacy "Agendar sessões…" preenche datas no backend apenas para sessões
      // que ainda têm scheduled_at (ver Fase 1.3 — ajuste mínimo).
    }];
    setA({ ...a, treatment_plan: plan });
    await Assessments.updateTreatmentPlan(id, plan);
    toast.success("Sessão adicionada");
  };

  // ---- Sessões: editor humano (sem datas auto) ----
  const updateSessionField = (i, field, value) => {
    const plan = structuredClone(a.treatment_plan || {});
    const arr = plan.sessions || [];
    if (!arr[i]) return;
    arr[i][field] = value;
    plan.sessions = arr;
    saveTreatmentPlan(plan);
  };
  const moveSession = (i, dir) => {
    const plan = structuredClone(a.treatment_plan || {});
    const arr = plan.sessions || [];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    // renumera
    arr.forEach((s, k) => { s.session_number = k + 1; });
    plan.sessions = arr;
    saveTreatmentPlan(plan);
  };
  const removeSession = (i) => {
    const plan = structuredClone(a.treatment_plan || {});
    const arr = (plan.sessions || []).filter((_, k) => k !== i);
    arr.forEach((s, k) => { s.session_number = k + 1; });
    plan.sessions = arr;
    saveTreatmentPlan(plan);
    toast.success("Sessão removida");
  };

  // ---- Cadência semântica & estado do plano ----
  const CADENCE_PT = { semanal: "Semanal", quinzenal: "Quinzenal", mensal: "Mensal" };
  const cadenceOf = (s) => {
    const t = (s?.type || "").toLowerCase();
    if (t.includes("seman")) return "semanal";
    if (t.includes("quinz")) return "quinzenal";
    return "mensal";
  };
  const planStatus = (() => {
    const total = (a?.treatment_plan?.sessions || []).length;
    const scheduled = appointments.length;
    if (!total) return { key: "none", label: "Sem plano", tone: "slate" };
    if (scheduled === 0) return { key: "suggested", label: "Plano sugerido · não agendado", tone: "sky" };
    if (scheduled < total) return { key: "partial", label: `Parcialmente agendado (${scheduled}/${total})`, tone: "amber" };
    return { key: "active", label: `Plano ativo · ${scheduled} sessões`, tone: "emerald" };
  })();
  const planTechniques = (a?.treatment_plan?.clinical_phases || []).flatMap((phase) => phase.techniques || []);
  const planExercises = (a?.treatment_plan?.home_plan || []).flatMap((group) => group.exercises || []);
  const patientVisibleCount = [...planTechniques, ...planExercises].filter(isPatientVisible).length;

  const onVasChange = async (v) => {
    setA({ ...a, vas_score: v });
    try { await Assessments.setVas(id, v); }
    catch { toast.error("Não foi possível guardar a VAS"); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="assessment-workspace">
      <Link to={`/patients/${a.patient_id}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar ao paciente
      </Link>
      <ConsentNotice patientId={a.patient_id} op="intervencao" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Avaliação Funcional</div>
          <h1 className="font-display text-3xl text-slate-50 mt-1">Espaço de avaliação</h1>
          <div className="text-slate-500 text-sm mt-1">{new Date(a.created_at).toLocaleString("pt-PT")}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="ai-detect-btn" variant="outline" onClick={detectAI} disabled={busy==="ai"} className="rounded-full border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
            <Brain className="w-4 h-4 mr-2" /> {busy==="ai" ? "MediaPipe a analisar…" : "Auto-deteção (MediaPipe)"}
          </Button>
          {autoConfidence != null && (
            <span
              data-testid="auto-confidence-badge"
              className={`text-[11px] px-2.5 py-1 rounded-full border font-mono tracking-wider ${
                autoConfidence >= 0.7 ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                : autoConfidence >= 0.4 ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                : "bg-rose-500/10 border-rose-500/40 text-rose-300"
              }`}
              title="Confiança média do MediaPipe nos pontos auto-detetados"
            >
              {Math.round(autoConfidence * 100)}% conf.
            </span>
          )}
          {silhouetteWarn && (
            <span
              data-testid="silhouette-warn-badge"
              title="Refinamento da silhueta lateral com confiança baixa — valida os pontos da coluna manualmente."
              className="text-[11px] px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono tracking-wider inline-flex items-center gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Sugestão automática — validar pontos
            </span>
          )}
          <Button data-testid="save-landmarks-btn" variant="outline" onClick={saveLandmarks} disabled={busy==="save"} className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Guardar pontos
          </Button>
          <Button data-testid="run-analysis-btn" onClick={runAnalysis} disabled={busy==="analyze"} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6 glow-sky">
            <Sparkles className="w-4 h-4 mr-2" /> {busy==="analyze" ? "A analisar…" : "Analisar"}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Mobile: scroll horizontal touch-friendly · Desktop: pill row inline */}
        <div className="x-scroll -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-slate-900/50 border border-slate-800 rounded-full p-1 gap-1 inline-flex w-max sm:w-auto">
            <TabsTrigger value="landmarks" data-testid="tab-landmarks" className="rounded-full whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950">Aquisição</TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results" className="rounded-full whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950">Resumo Funcional</TabsTrigger>
            <TabsTrigger value="spine" data-testid="tab-spine" className="rounded-full whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950">Análise Clínica</TabsTrigger>
            <TabsTrigger value="treatment" data-testid="tab-treatment" className="rounded-full whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950">Plano Terapêutico</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report" className="rounded-full whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950">Relatório</TabsTrigger>
          </TabsList>
        </div>

        {/* LANDMARKS */}
        <TabsContent value="landmarks" className="mt-6">
          <div className="mb-6">
            <VasScale value={a.vas_score ?? 0} onChange={onVasChange} />
          </div>

          {/* Grid overlay controls */}
          <div data-testid="grid-controls" className="mb-4 flex items-center gap-3 flex-wrap rounded-2xl border border-slate-800/80 bg-slate-900/40 px-3 sm:px-4 py-3 min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Grelha</span>
              <button
                data-testid="grid-toggle"
                onClick={() => updateGrid({ show: !grid.show })}
                className={`px-3 py-1 rounded-full text-[11px] border transition ${grid.show ? "bg-sky-500/15 border-sky-500/40 text-sky-200" : "bg-slate-800/40 border-slate-700 text-slate-400"}`}
              >
                {grid.show ? "Mostrar grelha · ON" : "Mostrar grelha · OFF"}
              </button>
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-[160px] sm:min-w-[180px] sm:max-w-[260px]">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 w-14 shrink-0">Tamanho</span>
              <input
                data-testid="grid-size"
                type="range" min={16} max={96} step={2}
                value={grid.size}
                onChange={(e) => updateGrid({ size: Number(e.target.value) })}
                disabled={!grid.show}
                className="flex-1 min-w-0 accent-sky-400 disabled:opacity-30"
              />
              <span className="text-[10px] font-mono text-slate-500 w-8 text-right shrink-0">{grid.size}px</span>
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-[160px] sm:min-w-[180px] sm:max-w-[260px]">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 w-14 shrink-0">Opacidade</span>
              <input
                data-testid="grid-opacity"
                type="range" min={0.05} max={1.0} step={0.05}
                value={grid.opacity}
                onChange={(e) => updateGrid({ opacity: Number(e.target.value) })}
                disabled={!grid.show}
                className="flex-1 min-w-0 accent-sky-400 disabled:opacity-30"
              />
              <span className="text-[10px] font-mono text-slate-500 w-10 text-right">{Math.round(grid.opacity * 100)}%</span>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <button
                data-testid="grid-mode-analysis"
                onClick={() => updateGrid({ show: true, opacity: 0.30, size: 32 })}
                className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border border-violet-500/30 bg-violet-500/5 text-violet-200 hover:bg-violet-500/10"
                title="Grelha visível, ajuda alinhamento e proporções"
              >
                Modo análise
              </button>
              <button
                data-testid="grid-mode-clinical"
                onClick={() => updateGrid({ show: false })}
                className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/10"
                title="Sem grelha, foco apenas nos landmarks"
              >
                Modo clínico
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-slate-300 mb-2">Vista frontal</div>
              {a.front_image ? (
                <PinchZoomFrame>
                <LandmarkCanvas
                  image={a.front_image}
                  landmarks={a.landmarks_front}
                  view="front"
                  showSkeleton
                  segments={a.analysis?.segments}
                  showGrid={grid.show}
                  gridSize={grid.size}
                  gridOpacity={grid.opacity}
                  onChange={(lm) => setA({ ...a, landmarks_front: lm })}
                />
                </PinchZoomFrame>
              ) : <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-slate-500 text-center bg-slate-900/30">Sem imagem frontal</div>}
            </div>
            <div>
              <div className="text-sm text-slate-300 mb-2">Vista lateral</div>
              {a.side_image ? (
                <PinchZoomFrame>
                <LandmarkCanvas
                  image={a.side_image}
                  landmarks={a.landmarks_side}
                  view="side"
                  showSkeleton
                  showSpine
                  showPlumb
                  segments={a.analysis?.segments}
                  plumbLine={a.analysis?.plumb_line}
                  sagittal={sagittal}
                  showGrid={grid.show}
                  gridSize={grid.size}
                  gridOpacity={grid.opacity}
                  onChange={(lm) => setA({ ...a, landmarks_side: lm })}
                />
                </PinchZoomFrame>
              ) : <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-slate-500 text-center bg-slate-900/30">Sem imagem lateral</div>}
            </div>
            <div>
              <div className="text-sm text-slate-300 mb-2">Vista posterior</div>
              {a.back_image ? (
                <PinchZoomFrame>
                <LandmarkCanvas
                  image={a.back_image}
                  landmarks={a.landmarks_back}
                  view="back"
                  showSkeleton
                  showSpine
                  segments={a.analysis?.segments}
                  showGrid={grid.show}
                  gridSize={grid.size}
                  gridOpacity={grid.opacity}
                  onChange={(lm) => setA({ ...a, landmarks_back: lm })}
                />
                </PinchZoomFrame>
              ) : <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-slate-500 text-center bg-slate-900/30">Sem imagem posterior</div>}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Arrasta cada ponto para o ajustar. Usa o botão <span className="text-sky-400">Auto-deteção (MediaPipe)</span> para detetar automaticamente os pontos anatómicos no dispositivo (modelo HEAVY, sem enviar imagens para o servidor).
          </p>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-400 font-mono">
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#7dd3fc",boxShadow:"0 0 8px rgba(56,189,248,0.8)"}}/>Alinhado</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#6ee7b7",boxShadow:"0 0 8px rgba(52,211,153,0.7)"}}/>Desvio ligeiro</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#fcd34d",boxShadow:"0 0 8px rgba(251,191,36,0.7)"}}/>Moderado</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#fda4af",boxShadow:"0 0 8px rgba(244,63,94,0.8)"}}/>Significativo</span>
          </div>
        </TabsContent>

        {/* RESULTS */}
        <TabsContent value="results" className="mt-6">
          {!a.analysis ? (
            <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
              Ainda sem resultados. Marca os pontos e clica em <span className="text-sky-400">Analisar postura</span>.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Evolução clínica — só aparece se houver avaliação anterior analisada */}
              <EvolutionInsights assessment={a} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 flex flex-col items-center glow-sky">
                <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold mb-4">Pontuação de Estado Funcional</div>
                <PostureScoreRing score={a.analysis.global_score} classification={a.analysis.classification} />
                <div className="mt-6 w-full space-y-2">
                  {Object.entries(a.analysis.segments).map(([k, seg]) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{SEG_PT[k]}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-200">{seg.score}</span>
                        <SeverityBadge sev={seg.severity} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 space-y-6">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Achados funcionais por segmento</div>
                  <div className="mt-4 space-y-4">
                    {Object.entries(a.analysis.segments).map(([k, seg]) => (
                      <div key={k} className="border-l-2 pl-4" style={{ borderColor: seg.severity === "normal" ? "#34d399" : seg.severity === "moderado" ? "#facc15" : "#f43f5e" }}>
                        <div className="flex items-center gap-3">
                          <span className="font-display text-lg text-slate-50">{SEG_PT[k]}</span>
                          <SeverityBadge sev={seg.severity} />
                          <span className="text-xs text-slate-500 ml-auto">{seg.score}/100</span>
                        </div>
                        <ul className="text-sm text-slate-300 mt-1 list-disc list-inside space-y-0.5">
                          {seg.findings.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
                {a.analysis.patterns?.length > 0 && (
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 glow-emerald">
                    <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold">Padrões funcionais identificados</div>
                    <div className="mt-4 space-y-3">
                      {a.analysis.patterns.map((p, i) => (
                        <div key={i}>
                          <div className="text-slate-50 font-medium">{p.name}</div>
                          <div className="text-sm text-slate-400">{p.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {a.analysis.chains?.length > 0 && (
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Cadeias biomecânicas</div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {a.analysis.chains.map((c, i) => (
                        <div key={i} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-slate-50 font-medium">{c.name}</div>
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-sky-500/30 text-sky-300 bg-sky-500/10">{c.status}</span>
                          </div>
                          <div className="text-sm text-slate-400 mt-2">{c.description}</div>
                          {c.markers?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {c.markers.map((m, j) => (
                                <span key={j} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">{m}</span>
                              ))}
                            </div>
                          )}
                          {c.targets?.length > 0 && (
                            <div className="mt-2 text-[11px] text-slate-500">Alvos: <span className="text-slate-300">{c.targets.join(" · ")}</span></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {a.analysis.consequences?.length > 0 && (
                  <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">Possíveis implicações funcionais (não-diagnóstico)</div>
                    <ul className="mt-3 space-y-1.5 list-disc list-inside text-sm text-slate-300">
                      {a.analysis.consequences.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {a.analysis.visceral_insights?.length > 0 && (
                  <FeatureGate feature="visceral">
                    <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-6" data-testid="visceral-insights">
                      <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold">Observações pré-viscerais (sugestivas)</div>
                      <ul className="mt-3 space-y-1.5 list-disc list-inside text-sm text-slate-300">
                        {a.analysis.visceral_insights.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                      <p className="text-[11px] text-slate-500 mt-3 italic">
                        Observações estruturais que podem estar associadas a influências funcionais internas. Não constituem diagnóstico — sugestões de exploração clínica.
                      </p>
                    </div>
                  </FeatureGate>
                )}
                {(a.ai_interpretation || a.ai_patient_summary) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Interpretação funcional</div>
                      <p className="text-slate-300 text-sm mt-3 leading-relaxed">{a.ai_interpretation}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Explicação para o paciente</div>
                      <p className="text-slate-300 text-sm mt-3 leading-relaxed">{a.ai_patient_summary}</p>
                    </div>
                  </div>
                )}
                <SessionContextPanel assessmentId={id} />
                <PatientSummaryEditor assessmentId={id} />
              </div>
            </div>
            </div>
          )}
        </TabsContent>

        {/* SPINE */}
        {/* CLINICAL ANALYSIS (ex-spine / Mapeamento) */}
        <TabsContent value="spine" className="mt-6">
          {!a.analysis ? (
            <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
              Executa a análise para ver a interpretação clínica.
            </div>
          ) : (
            <div className="space-y-6" data-testid="clinical-analysis">
              {/* Header — métricas-chave */}
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold">Análise Clínica</div>
                  <h2 className="font-display text-2xl text-slate-50">Interpretação biomecânica funcional</h2>
                  <div className="text-xs text-slate-500 mt-1">Síntese dos dados detetados — linguagem não-diagnóstica.</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="focus-mode-toggle"
                    onClick={() => setFocusModeOn(v => !v)}
                    aria-pressed={focusModeOn}
                    className={`text-[11px] px-3 py-1.5 rounded-full border font-mono uppercase tracking-[0.15em] transition ${
                      focusModeOn
                        ? "bg-sky-500/15 border-sky-500/40 text-sky-200"
                        : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"
                    }`}
                    title="Focus Mode — destacar zona corporal com vignette subtil"
                  >
                    Focus {focusModeOn ? "ON" : "OFF"}
                  </button>
                  <button
                    type="button"
                    data-testid="heatmap-toggle"
                    onClick={() => setHeatmapOn(v => !v)}
                    aria-pressed={heatmapOn}
                    className={`text-[11px] px-3 py-1.5 rounded-full border font-mono uppercase tracking-[0.15em] transition ${
                      heatmapOn
                        ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
                        : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Heatmap {heatmapOn ? "ON" : "OFF"}
                  </button>
                  {a.analysis.global_score != null && (
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
                      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-mono">Estado funcional</span>
                      <span className="font-mono text-slate-100 text-lg">{a.analysis.global_score}<span className="text-xs text-slate-500">/100</span></span>
                    </div>
                  )}
                </div>
              </div>

              {/* Métricas biomecânicas chave (eixo gravitacional + simetrias) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="biomechanical-metrics">
                {(a.analysis.plumb_line?.deviations || []).slice(0, 4).map((d, i) => {
                  const tone = d.status === "alinhado" ? "emerald" : d.status === "moderado" ? "amber" : "rose";
                  const cls = {
                    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
                    amber:   "border-amber-500/30 bg-amber-500/5 text-amber-300",
                    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-300",
                  }[tone];
                  return (
                    <div key={i} className={`rounded-xl border ${cls} p-4`} data-testid={`metric-plumb-${d.point}`}>
                      <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">Linha de gravidade</div>
                      <div className="capitalize text-slate-50 font-display text-base mt-1">{({ ear: "Orelha", shoulder: "Ombro", hip: "Anca", knee: "Joelho", ankle: "Tornozelo" })[String(d.point).toLowerCase()] || d.point}</div>
                      <div className="font-mono text-2xl mt-1">Δ {(d.dx * 100).toFixed(1)}<span className="text-xs opacity-70">%</span></div>
                      <div className="text-[10px] opacity-80 mt-0.5 capitalize">{d.status}</div>
                    </div>
                  );
                })}
                {(!a.analysis.plumb_line?.deviations?.length) && (
                  <div className="col-span-full rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-500 italic">
                    Sem desvios significativos detetados na linha de gravidade.
                  </div>
                )}
              </div>

              {/* Mapa Biomecânico Virtual — interpretação separada da foto.
                  Renderiza apenas quando o utilizador ativa HEATMAP no toggle. */}
              {heatmapOn && (
                <VirtualBiomechanicalMap analysis={a.analysis} />
              )}

              {/* Vista lateral + observações da coluna */}
              {a.side_image && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <PinchZoomFrame>
                    <LandmarkCanvas
                      image={a.side_image}
                      landmarks={a.landmarks_side}
                      view="side"
                      showSpine
                      showPlumb
                      showSkeleton={false}
                      segments={a.analysis?.segments}
                      plumbLine={a.analysis?.plumb_line}
                      sagittal={sagittal}
                      heatmap={heatmapOn}
                      focusMode={focusModeOn}
                      onChange={(lm) => setA({ ...a, landmarks_side: lm })}
                    />
                  </PinchZoomFrame>
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Eixo axial · vista sagital</div>
                      <p className="text-sm text-slate-400 mt-2">
                        Curva da coluna e linha vertical de referência (ligeiramente anterior ao maléolo lateral), com os desvios estimados de cada segmento.
                      </p>
                    </div>
                    {a.analysis?.plumb_line?.deviations?.length > 0 && (
                      <PlumbRuler
                        deviations={a.analysis.plumb_line.deviations}
                        heightCm={patientLite?.height_cm}
                      />
                    )}
                    {a.analysis?.segments?.spine?.findings?.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Observações</div>
                        <ul className="text-sm text-slate-300 mt-2 list-disc list-inside space-y-0.5">
                          {a.analysis.segments.spine.findings.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {a.side_image && (
                <SagittalAlignmentCard result={sagittal} variant="professional" orientationValue={a.sagittal_orientation || ""} onOrientationChange={setOrientation} />
              )}

              {/* Insights Funcionais — sempre presente. Fallback gracioso quando o backend não devolveu padrões/cadeias. */}
              <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-6" data-testid="functional-insights">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-violet-300" strokeWidth={1.5} />
                  <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold">Insights Funcionais</div>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 italic">Padrões compatíveis com observações estruturais — não constituem diagnóstico.</p>

                {a.analysis.patterns?.length > 0 && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {a.analysis.patterns.map((p, i) => (
                      <div key={i} className="rounded-xl border border-violet-500/20 bg-slate-950/40 p-4" data-testid={`pattern-${i}`}>
                        <div className="text-slate-50 font-medium">{p.name}</div>
                        <div className="text-sm text-slate-400 mt-1">{p.description}</div>
                      </div>
                    ))}
                  </div>
                )}

                {a.analysis.chains?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono mb-2">Cadeias biomecânicas</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {a.analysis.chains.map((c, i) => (
                        <div key={i} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4" data-testid={`chain-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-slate-50 font-medium">{c.name}</div>
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-sky-500/30 text-sky-300 bg-sky-500/10">{c.status}</span>
                          </div>
                          {c.description && <div className="text-sm text-slate-400 mt-2">{c.description}</div>}
                          {c.markers?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {c.markers.map((m, j) => (
                                <span key={j} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">{m}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!(a.analysis.patterns?.length > 0) && !(a.analysis.chains?.length > 0) && (
                  <div className="mt-4 rounded-xl border border-dashed border-violet-500/20 bg-slate-950/30 p-4 text-sm text-slate-400" data-testid="functional-insights-empty">
                    Sem padrões ou cadeias dominantes detetados nos dados atuais. A leitura permanece compatível com um perfil estrutural alinhado.
                  </div>
                )}
              </div>

              {/* Raciocínio clínico contextual — MIO Matrix */}
              <ClinicalReasoningPanel analysis={a.analysis} assessmentId={id} />

              {/* Implicações funcionais (consequences) — sempre presente, com fallback. */}
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6" data-testid="functional-implications">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">Possíveis implicações funcionais</div>
                {a.analysis.consequences?.length > 0 ? (
                  <ul className="mt-3 space-y-1.5 list-disc list-inside text-sm text-slate-300">
                    {a.analysis.consequences.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-slate-400 italic" data-testid="functional-implications-empty">
                    Os achados funcionais são subtis; o acompanhamento regular mantém o equilíbrio postural ao longo do tempo.
                  </p>
                )}
              </div>

              {/* Precauções Clínicas / Red Flags — sempre presente. */}
              <RedFlagsPanel patient={patientLite} analysis={a.analysis} assessmentId={id} />
            </div>
          )}
        </TabsContent>

        {/* TREATMENT */}
        <TabsContent value="treatment" className="mt-6">
          {!a.treatment_plan ? (
            <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
              Executa a análise para gerar o plano de tratamento.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Plano Terapêutico</div>
                  <h2 className="font-display text-2xl text-slate-50">Sugestão clínica editável</h2>
                  <div className="text-xs text-slate-500 mt-1">Ponto de partida gerado pela IA — adapta livremente ao paciente. Não constitui prescrição.</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    data-testid="plan-status-badge"
                    className={`text-[11px] px-3 py-1.5 rounded-full border font-mono uppercase tracking-[0.15em] ${
                      planStatus.tone === "emerald" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" :
                      planStatus.tone === "amber"   ? "bg-amber-500/10 border-amber-500/40 text-amber-300" :
                      planStatus.tone === "sky"     ? "bg-sky-500/10 border-sky-500/40 text-sky-300" :
                                                      "bg-slate-800 border-slate-700 text-slate-400"
                    }`}
                  >
                    {planStatus.label}
                  </span>
                  <Button data-testid="generate-appointments-btn" onClick={openScheduleDialog} disabled={busy==="cal" || !(a.treatment_plan?.sessions || []).length} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full px-5 glow-emerald">
                    <CalendarPlus className="w-4 h-4 mr-2" /> Agendar sessões…
                  </Button>
                </div>
              </div>

              <div
                className="rounded-xl border border-sky-500/25 bg-sky-500/5 p-4 text-sm text-slate-300"
                data-testid="patient-content-review"
              >
                <div className="font-medium text-sky-200">Conteúdos visíveis no Portal do Paciente</div>
                <p className="mt-1 text-xs text-slate-400">
                  {patientVisibleCount} de {planTechniques.length + planExercises.length} conteúdos selecionados.
                  Os novos conteúdos ficam ocultos até serem assinalados; revê cada seleção antes de partilhar.
                  Conteúdos não assinalados continuam apenas no espaço profissional.
                </p>
              </div>

              {/* Fases clínicas */}
              <div className="space-y-4" data-testid="clinical-phases">
                {(a.treatment_plan.clinical_phases || []).map((ph, pi) => {
                  const accent = ph.phase === 1 ? "sky" : ph.phase === 2 ? "emerald" : "violet";
                  const accentRing = {
                    sky:     "border-sky-500/30 bg-sky-500/5",
                    emerald: "border-emerald-500/30 bg-emerald-500/5",
                    violet:  "border-violet-500/30 bg-violet-500/5",
                  }[accent];
                  const accentText = {
                    sky: "text-sky-300", emerald: "text-emerald-300", violet: "text-violet-300",
                  }[accent];
                  return (
                    <div key={pi} data-testid={`phase-${ph.phase}`} className={`rounded-2xl border ${accentRing} p-6`}>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full border ${accentRing} font-display text-sm ${accentText}`}>
                          {ph.phase}
                        </span>
                        <div>
                          <div className={`font-display text-lg text-slate-50`}>{ph.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{ph.goal}</div>
                        </div>
                      </div>
                      <div className="mt-4 space-y-3">
                        {(ph.techniques || []).length === 0 ? (
                          <div className="text-xs text-slate-500 italic">Sem técnicas indicadas nesta fase para esta avaliação.</div>
                        ) : (
                          ph.techniques.map((t, ti) => (
                            <div key={ti} data-testid={`technique-${ph.phase}-${ti}`} className="flex items-start justify-between gap-3 p-4 rounded-xl border border-slate-800 bg-slate-950/50 hover:border-sky-500/30">
                              <div className="flex-1">
                                <input
                                  className="bg-transparent text-slate-50 font-medium w-full focus:outline-none focus:bg-slate-900/60 rounded px-1 -mx-1"
                                  value={t.name || ""}
                                  onChange={(e) => updateTechniqueField(pi, ti, "name", e.target.value)}
                                  data-testid={`edit-technique-name-${ph.phase}-${ti}`}
                                />
                                <div className={`text-xs ${accentText} mt-0.5`}>{t.category}</div>
                                {t.target_area && <div className="text-[11px] text-slate-400 mt-0.5">Área-alvo: <span className="text-slate-200">{t.target_area}</span></div>}
                                {t.description && <div className="text-sm text-slate-400 mt-2">{t.description}</div>}
                                <div className="text-xs text-emerald-400 mt-2"><span className="text-slate-500">Objetivo: </span>{t.objective}</div>
                                {t.indication && <div className="text-xs text-slate-500 mt-1"><span className="text-slate-600">Indicação: </span>{t.indication}</div>}
                                {t.professional_only ? (
                                  <div className="mt-3 text-[11px] text-violet-300" data-testid={`pro-only-technique-${ph.phase}-${ti}`}>Só profissional — não aparece no Portal do Paciente</div>
                                ) : (
                                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                                  <input
                                    type="checkbox"
                                    checked={isPatientVisible(t)}
                                    onChange={(e) => updateTechniqueField(pi, ti, "patient_visible", e.target.checked)}
                                    data-testid={`patient-visible-technique-${ph.phase}-${ti}`}
                                    className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500"
                                  />
                                  Disponibilizar no Portal do Paciente
                                </label>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 items-center">
                                <button
                                  className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                                  onClick={() => moveTechnique(pi, ti, -1)}
                                  disabled={ti === 0}
                                  data-testid={`move-up-${ph.phase}-${ti}`}
                                  title="Mover para cima"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                                  onClick={() => moveTechnique(pi, ti, +1)}
                                  disabled={ti === ph.techniques.length - 1}
                                  data-testid={`move-down-${ph.phase}-${ti}`}
                                  title="Mover para baixo"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-slate-600 hover:text-rose-400"
                                  onClick={() => removeTechnique(pi, ti)}
                                  data-testid={`remove-technique-${ph.phase}-${ti}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button size="sm" variant="outline"
                          data-testid={`add-technique-${ph.phase}`}
                          onClick={() => addCustomTechnique(pi)}
                          className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-800 text-xs">
                          <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> Adicionar técnica
                        </Button>
                        <Button size="sm" variant="outline"
                          data-testid={`add-from-library-${ph.phase}`}
                          onClick={() => setLibCtx({ type: "clinical", phaseIdx: pi, phase: ph.phase })}
                          className="rounded-full border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs">
                          <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Adicionar da biblioteca
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Plano domiciliário */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6" data-testid="home-plan">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-display">H</span>
                  <div>
                    <div className="font-display text-lg text-slate-50">Plano de Exercícios em Casa</div>
                    <div className="text-xs text-slate-400 mt-0.5">Complemento ao tratamento clínico — a realizar entre sessões.</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(a.treatment_plan.home_plan || []).map((grp, gi) => (
                    <div key={gi} data-testid={`home-group-${grp.group}`} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">{grp.name}</div>
                        <div className="flex gap-1.5">
                          <button
                            data-testid={`add-home-${grp.group}`}
                            onClick={() => addCustomHomeExercise(gi)}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800"
                            title="Exercício personalizado"
                          >
                            <PlusCircle className="w-3 h-3 inline -mt-0.5 mr-1" /> +
                          </button>
                          <button
                            data-testid={`add-from-library-home-${grp.group}`}
                            onClick={() => setLibCtx({ type: "home", groupIdx: gi, group: grp.group })}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                            title="Biblioteca"
                          >
                            <BookOpen className="w-3 h-3 inline -mt-0.5 mr-1" /> Lib
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 space-y-3">
                        {(grp.exercises || []).length === 0 ? (
                          <div className="text-[11px] text-slate-600 italic py-2">Sem exercícios neste grupo.</div>
                        ) : (
                          grp.exercises.map((e, ei) => (
                            <div key={ei} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <button type="button" onClick={() => updateHomeExerciseField(gi, ei, "priority", !e.priority)} title={e.priority ? "Prioritário (remover)" : "Marcar como prioritário"}
                                    aria-pressed={!!e.priority} data-testid={`priority-exercise-${grp.group}-${ei}`} className={e.priority ? "text-amber-300" : "text-slate-600 hover:text-amber-300"}>
                                    <Star className="w-4 h-4" fill={e.priority ? "currentColor" : "none"} />
                                  </button>
                                  <div className="text-slate-100 font-medium text-sm">{e.name}</div>
                                </div>
                                {e.target_area && <div className="text-[11px] text-slate-400">Área-alvo: <span className="text-slate-200">{e.target_area}</span></div>}
                                <div className="text-[11px] text-emerald-400 mt-1">{e.objective}</div>
                                <div className="flex gap-3 mt-1 text-[11px] text-slate-500">
                                  {e.reps && <span><span className="text-slate-600">Reps: </span>{e.reps}</span>}
                                  {e.frequency && <span><span className="text-slate-600">Freq: </span>{e.frequency}</span>}
                                </div>
                                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                                  <input
                                    type="checkbox"
                                    checked={isPatientVisible(e)}
                                    onChange={(event) => updateHomeExerciseField(gi, ei, "patient_visible", event.target.checked)}
                                    data-testid={`patient-visible-exercise-${grp.group}-${ei}`}
                                    className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500"
                                  />
                                  Disponibilizar no Portal do Paciente
                                </label>
                              </div>
                              <div className="flex flex-col gap-1 items-center">
                                <button
                                  onClick={() => moveHomeExercise(gi, ei, -1)}
                                  disabled={ei === 0}
                                  className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                                  data-testid={`move-home-up-${grp.group}-${ei}`}
                                >
                                  <ChevronUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => moveHomeExercise(gi, ei, +1)}
                                  disabled={ei === grp.exercises.length - 1}
                                  className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                                  data-testid={`move-home-down-${grp.group}-${ei}`}
                                >
                                  <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className="text-slate-600 hover:text-rose-400"
                                  onClick={() => removeHomeExercise(gi, ei)}
                                  data-testid={`remove-home-${grp.group}-${ei}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sessões — editor humano, sem datas automáticas. Cadência semântica + foco editável. */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6" data-testid="sessions-editor">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm text-slate-300 font-medium">Sessões sugeridas</div>
                    <div className="text-xs text-slate-500">Edita cadência e foco. Sem datas reais — agenda manualmente quando estiver pronto.</div>
                  </div>
                  <Button variant="ghost" className="text-sky-400 hover:bg-sky-500/10" onClick={addSession} data-testid="add-session-btn">
                    <PlusCircle className="w-4 h-4 mr-1.5" /> Sessão
                  </Button>
                </div>
                {(a.treatment_plan.sessions || []).length === 0 ? (
                  <div className="text-[12px] text-slate-500 italic py-4">Sem sessões. Clica em <span className="text-sky-400">+ Sessão</span> para começar.</div>
                ) : (
                  <div className="space-y-2">
                    {(a.treatment_plan.sessions || []).map((s, i) => {
                      const cad = cadenceOf(s);
                      const cadTone = cad === "semanal" ? "sky" : cad === "quinzenal" ? "emerald" : "violet";
                      const cadCls = {
                        sky:     "bg-sky-500/10 border-sky-500/30 text-sky-300",
                        emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
                        violet:  "bg-violet-500/10 border-violet-500/30 text-violet-300",
                      }[cadTone];
                      return (
                        <div key={i} data-testid={`session-row-${i}`} className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 bg-slate-950/40 hover:border-sky-500/30 transition">
                          <div className="w-8 h-8 shrink-0 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs flex items-center justify-center font-mono mt-0.5">
                            {s.session_number || i + 1}
                          </div>
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-[0.12em] ${cadCls}`}>
                                {CADENCE_PT[cad]} · S{s.session_number || i + 1}
                              </span>
                              <select
                                data-testid={`session-cadence-${i}`}
                                value={cad}
                                onChange={(e) => updateSessionField(i, "type", e.target.value)}
                                className="bg-slate-900 border border-slate-700 text-slate-200 text-[11px] rounded-md px-2 py-0.5 focus:outline-none focus:border-sky-500"
                              >
                                <option value="semanal">Semanal</option>
                                <option value="quinzenal">Quinzenal</option>
                                <option value="mensal">Mensal</option>
                              </select>
                            </div>
                            <input
                              data-testid={`session-focus-${i}`}
                              className="w-full bg-transparent text-sm text-slate-100 focus:outline-none focus:bg-slate-900/60 rounded px-1 -mx-1"
                              value={s.focus || ""}
                              placeholder="Foco da sessão (ex: avaliação inicial · técnicas miofasciais)"
                              onChange={(e) => updateSessionField(i, "focus", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col gap-0.5 items-center">
                            <button
                              className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                              onClick={() => moveSession(i, -1)}
                              disabled={i === 0}
                              data-testid={`session-up-${i}`}
                              title="Mover para cima"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              className="text-slate-600 hover:text-sky-300 disabled:opacity-30"
                              onClick={() => moveSession(i, +1)}
                              disabled={i === (a.treatment_plan.sessions.length - 1)}
                              data-testid={`session-down-${i}`}
                              title="Mover para baixo"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              className="text-slate-600 hover:text-rose-400"
                              onClick={() => removeSession(i)}
                              data-testid={`session-remove-${i}`}
                              title="Remover sessão"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* REPORT */}
        <TabsContent value="report" className="mt-6">
          {!a.analysis ? (
            <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
              Executa a análise para gerar o relatório.
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Relatório funcional</div>
                  <h2 className="font-display text-2xl text-slate-50 mt-1">Pré-visualização</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="share-portal-btn"
                    variant="outline"
                    onClick={() => setShareOpen(true)}
                    className="rounded-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  >
                    <Share2 className="w-4 h-4 mr-2" /> Partilhar com paciente
                  </Button>
                  <a
                    data-testid="download-pdf-btn"
                    href={Assessments.pdfUrl(id)}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 px-5 py-2.5 font-semibold glow-sky"
                  >
                    <Download className="w-4 h-4" /> Descarregar PDF
                  </a>
                </div>
              </div>
              <div className="mt-6 rounded-xl overflow-hidden border border-slate-800 bg-white h-[80vh]">
                <iframe title="PDF preview" src={Assessments.pdfUrl(id)} className="w-full h-full" />
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ShareDialog assessmentId={id} patientId={a?.patient_id} open={shareOpen} onOpenChange={setShareOpen} />

      <ScheduleSessionsDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        assessment={a}
        appointments={appointments}
        onScheduled={async () => { await loadAppointments(); await load(); }}
      />

      <AddFromLibraryDialog
        open={!!libCtx}
        context={libCtx ? {
          type: libCtx.type,
          phase: libCtx.phase,
          group: libCtx.group,
        } : null}
        onOpenChange={(v) => { if (!v) setLibCtx(null); }}
        onAdd={addItemFromLibrary}
      />
    </div>
  );
}
